import clsx from 'clsx'
import { deepEqual } from 'fast-equals'
import { create } from '@bufbuild/protobuf'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import type { Offer } from '@jotta/grpc-connect-openapi/offer'
import { Offer_ProductScope } from '@jotta/grpc-connect-openapi/offer'
import type { Money } from '@jotta/grpc-connect-openapi/payment'
import {
  MoneySchema,
  RecurringSubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { useLocale } from '@jotta/i18n'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Divider } from '@jotta/ui/Divider'
import SvgLogoIcon from '@jotta/ui/SvgLogo_iconLight'
import { cn } from '@jotta/utils/css'
import {
  formatPriceConnect,
  formatPriceConnectWithInterval,
} from '@jotta/utils/formatPrice'
import { formatStorage } from '@jotta/utils/formatStorage'

function SummaryPriceLine({
  interval,
  money,
  ...props
}: {
  labelText: string
  subtext: boolean
  className?: string
  money: Money
  interval?: RecurringSubscriptionInterval
  labelClassName?: string
  valueClassName?: string
}) {
  const locale = useLocale()
  return SummaryLine({
    value: interval
      ? formatPriceConnectWithInterval(interval, money, locale)
      : formatPriceConnect(money, locale),
    ...props,
  })
}

function SummaryLine({
  labelText,
  subtext,
  value,
  className,
  labelClassName,
  valueClassName,
}: {
  labelText: string
  subtext: boolean
  value?: string
  className?: string
  labelClassName?: string
  valueClassName?: string
}) {
  return (
    <div
      className={cn(
        'body-md-ln flex items-end justify-between py-1.5',
        {
          'text-signup-summary-subtext': subtext,
        },
        className,
      )}
    >
      <span className={labelClassName}>{labelText}</span>
      <span className={cn('text-right', valueClassName)}>{value}</span>
    </div>
  )
}

export function PaymentSummary({
  offer,
  interval,
  className,
}: {
  offer: Offer
  interval: RecurringSubscriptionInterval
  className?: string
}) {
  const locale = useLocale()
  const yearly = interval == RecurringSubscriptionInterval.YEARLY_RECURRING
  const payment = yearly ? offer.yearly : offer.monthly
  const basePrice = payment?.basePrice
  const baseStorage = offer.baseStorage
  const baseStorageFormatted = baseStorage && formatStorage(baseStorage)
  const recurringPrice = payment?.recurringPrice
  const modifiers = payment?.modifiers || []
  const vat = payment?.vat
  const billedNow = payment?.billedNow
  const subtotal =
    vat &&
    billedNow &&
    vat.currency === billedNow.currency &&
    create(MoneySchema, {
      amount: billedNow.amount - vat.amount,
      currency: vat.currency,
    })
  const displayVAT =
    offer.productScope === Offer_ProductScope.BUSINESS &&
    vat &&
    vat.amount > 0 &&
    subtotal
  const vatPercentage =
    displayVAT &&
    Math.round(Number(100n * vat.amount) / Number(subtotal.amount))
  const recurringRenewalPriceFormatted =
    recurringPrice && !deepEqual(recurringPrice, billedNow)
      ? formatPriceConnectWithInterval(interval, recurringPrice, locale)
      : undefined
  return (
    <div className={clsx('flex flex-col gap-2', className)}>
      <h2 className="title-sm pb-14">
        <Trans>Summary</Trans>
      </h2>

      <div>
        {basePrice && (
          <div className="flex items-center">
            <PlainBrandIcon icon={SvgLogoIcon} height={20} className="mr-4" />
            <SummaryPriceLine
              className="w-full"
              labelClassName="h1"
              labelText={offer.productNameLocalized}
              subtext={false}
              interval={interval}
              money={basePrice}
            />
          </div>
        )}

        {baseStorageFormatted && (
          <SummaryLine
            subtext={true}
            labelText={baseStorageFormatted}
            value={t`included`}
            className="pl-9"
          />
        )}

        {modifiers.length > 0 && <div className="pb-8" />}

        {modifiers.map(({ descriptionLocalized, amount }) => {
          return (
            amount && (
              <SummaryPriceLine
                subtext={true}
                key={descriptionLocalized}
                labelText={descriptionLocalized}
                money={amount}
              />
            )
          )
        })}
      </div>

      {displayVAT && (
        <>
          <Divider className="my-8" />
          <SummaryPriceLine
            subtext={true}
            labelText={`${t`Subtotal`} (${t`excl. VAT`})`}
            money={subtotal}
          />
          <SummaryPriceLine
            subtext={true}
            labelText={`${t`VAT`} (${vatPercentage}%)`}
            money={vat}
          />
        </>
      )}

      {billedNow && (
        <>
          <Divider className="my-8" />
          <SummaryPriceLine
            subtext={false}
            labelText={t`Billed Now` + (displayVAT ? ` (${t`incl. VAT`})` : '')}
            valueClassName="h2"
            labelClassName="h2"
            money={billedNow}
          />
        </>
      )}

      <p className="text-signup-summary-subtext">
        {recurringRenewalPriceFormatted && (
          <>
            <Trans>Then renewed at {recurringRenewalPriceFormatted}.</Trans>
            &nbsp;
          </>
        )}

        <Trans>
          You can cancel your subscription at any time before your next renewal.
        </Trans>
      </p>
    </div>
  )
}
